import { useEffect, useMemo } from 'react';
import { useRouterState } from '@tanstack/react-router';
import i18n from '@/i18n/config';
import { applyDocumentDirection } from '@/i18n/config';
import {
  getProductBranding,
  type ProductBrandingConfig,
  type ProductLocale,
} from '@/modules/auth/config/productBranding';
import { parseProductAuthContext } from '@/modules/auth/lib/parseProductAuthContext';

export interface ResolvedProductAuthBranding {
  branding: ProductBrandingConfig;
  locale: ProductLocale;
}

function searchStrToRecord(searchStr: string): Record<string, unknown> {
  const params = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr);
  const search: Record<string, unknown> = {};
  params.forEach((value, key) => {
    search[key] = value;
  });
  return search;
}

function resolveLocale(
  explicitLocale: ProductLocale | null,
  i18nLanguage: string | undefined,
): ProductLocale {
  if (explicitLocale) return explicitLocale;
  if (i18nLanguage?.startsWith('en')) return 'en';
  return 'ar';
}

function resolveDocumentLocale(language: string): ProductLocale {
  return language.startsWith('en') ? 'en' : 'ar';
}

export function useProductAuthBranding(): ResolvedProductAuthBranding | null {
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });

  const context = useMemo(() => parseProductAuthContext(searchStrToRecord(searchStr)), [searchStr]);

  const resolved = useMemo(() => {
    const branding = getProductBranding(context.productCode, 'ar');
    if (!branding) return null;

    const locale = resolveLocale(context.locale, i18n.resolvedLanguage ?? i18n.language);
    return { branding, locale };
  }, [context.productCode, context.locale]);

  useEffect(() => {
    if (!resolved) return;

    const previousLanguage = i18n.language;
    const shouldApplyLocale = Boolean(context.locale && i18n.language !== context.locale);

    if (shouldApplyLocale && context.locale) {
      void i18n.changeLanguage(context.locale);
    }
    applyDocumentDirection(resolved.locale);

    return () => {
      if (shouldApplyLocale) {
        void i18n.changeLanguage(previousLanguage);
        applyDocumentDirection(resolveDocumentLocale(previousLanguage));
      }
    };
  }, [resolved, context.locale]);

  useEffect(() => {
    if (!resolved) return;

    const existingLink = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    const createdLink = !existingLink;
    const link =
      existingLink ??
      (() => {
        const el = document.createElement('link');
        el.rel = 'icon';
        document.head.appendChild(el);
        return el;
      })();

    const previousFavicon = link.getAttribute('href');
    const previousTitle = document.title;

    link.type = 'image/png';
    link.href = resolved.branding.faviconUrl;
    document.title =
      resolved.locale === 'ar' ? resolved.branding.productNameAr : resolved.branding.productName;

    return () => {
      if (createdLink) {
        link.remove();
      } else if (previousFavicon) {
        link.href = previousFavicon;
      } else {
        link.removeAttribute('href');
      }
      document.title = previousTitle;
    };
  }, [resolved]);

  return resolved;
}
